import { analysisCsvFilename } from '../viewThruBatchView/BatchAnalysis'
import { BatchRun, csvEscape } from '../viewThruBatchView/shared'
import { batchLabel } from './useAllBatchesAnalysis'
import type { ImportExcludedRow, ImportPreview, ImportPreviewRow, PreviewState } from './useAllBatchesImport'

/**
 * The import preview is the final word on who goes into Sentinel Reporting
 * under the current waivers, so these exports come from it rather than from the
 * analysis: everyone the import will write, and everyone it will not.
 *
 * The files go to bookkeeping, who only need to identify the person: batch,
 * name, email and date of birth. Nothing else is exported.
 */

export type ImportListOutcome = 'importing' | 'not_importing'

export interface ImportListRow {
    batchId: string
    batchLabel: string
    outcome: ImportListOutcome
    name: string
    email: string
    /** ISO date, or empty when neither the batch nor a signature file had one. */
    dob: string
}

const fromPreviewRow = (run: BatchRun, item: ImportPreviewRow, outcome: ImportListOutcome): ImportListRow => ({
    batchId: run.id,
    batchLabel: batchLabel(run),
    outcome,
    name: item.name || '',
    email: item.email || '',
    dob: item.dob || '',
})

const fromExcludedRow = (run: BatchRun, item: ImportExcludedRow): ImportListRow => ({
    batchId: run.id,
    batchLabel: batchLabel(run),
    outcome: 'not_importing',
    name: item.name || '',
    email: item.email || '',
    dob: item.dob || '',
})

/** Every client one batch's preview accounts for, importing first. */
export const importListForPreview = (run: BatchRun, preview: ImportPreview): ImportListRow[] => [
    ...(preview.importable || []).map((item) => fromPreviewRow(run, item, 'importing')),
    ...(preview.blocked || []).map((item) => fromPreviewRow(run, item, 'not_importing')),
    ...(preview.alreadyImportedClients || []).map((item) => fromPreviewRow(run, item, 'not_importing')),
    ...(preview.notEligible || []).map((item) => fromExcludedRow(run, item)),
    ...(preview.duplicates || []).map((item) => fromExcludedRow(run, item)),
]

/**
 * The combined list across every batch with a completed preview, in the order
 * the batches are listed. Batches without a preview contribute nothing — the
 * preview is the only thing that knows the outcome.
 */
export const buildImportList = (batches: BatchRun[], previews: Record<string, PreviewState>) =>
    batches.flatMap((run) => {
        const state = previews[run.id]
        return state?.status === 'complete' ? importListForPreview(run, state.preview) : []
    })

export const importListByOutcome = (rows: ImportListRow[], outcome: ImportListOutcome) =>
    rows.filter((row) => row.outcome === outcome)

const IMPORT_LIST_CSV_COLUMNS: { header: string; value: (row: ImportListRow) => string }[] = [
    { header: 'batch', value: (row) => row.batchLabel },
    { header: 'client_name', value: (row) => row.name },
    { header: 'client_email', value: (row) => row.email },
    { header: 'date_of_birth', value: (row) => row.dob },
]

export const buildImportListCsv = (rows: ImportListRow[]) =>
    [
        IMPORT_LIST_CSV_COLUMNS.map((column) => csvEscape(column.header)).join(','),
        ...rows.map((row) => IMPORT_LIST_CSV_COLUMNS.map((column) => csvEscape(column.value(row))).join(',')),
    ].join('\n')

export const importListFilename = (outcome: ImportListOutcome, stamp: string, batchName?: string) =>
    analysisCsvFilename(batchName || 'viewthru-import', `${outcome === 'importing' ? 'importing' : 'not-importing'}-${stamp}`)
